import { createStore } from '@stencil/store';
import { EncryptStorage } from 'encrypt-storage';

const sessionEncryptStorage = new EncryptStorage('xxA85IVYI76rJ8kAqfGrZwNfZG4hQE14VCqyYBUNBqA34guziC', {
  storageType: 'sessionStorage',
});

// Max TTL age
export const BROWSER_STORAGE_TTL = {
  TTLNoExpired: -1,
  TTL30Minutes: 1000 * 60 * 30,
  TTL1Hour: 1000 * 60 * 60,
};

// Storage type enum
export const STORAGE_TYPE = {
  SESSION: 'session',
  LOCAL: 'local',
};

// Helper to get the appropriate storage instance
const getStorageInstance = (storageType = STORAGE_TYPE.SESSION) => {
  return storageType === STORAGE_TYPE.LOCAL ? window.localStorage : sessionEncryptStorage;
};

// Helper to get the appropriate item name
const getItemName = (name, storageType) => {
  return storageType === STORAGE_TYPE.LOCAL ? name : qualifyStoreName(name);
};

export const writeStoreToBrowserStorage = (
  store, 
  name, 
  ttl = BROWSER_STORAGE_TTL.TTL30Minutes, 
  storageType = STORAGE_TYPE.SESSION
) => {
  const itemName = getItemName(name, storageType);
  const expiresAt = ttl === BROWSER_STORAGE_TTL.TTLNoExpired ? -1 : new Date().getTime() + ttl;
  const { ttl: storeTtl, ...storeWithoutTtl } = store;
  const storeWithTTL = { ...storeWithoutTtl, ttl: expiresAt };
  
  if (storageType === STORAGE_TYPE.LOCAL) {
    window.localStorage.setItem(itemName, JSON.stringify(storeWithTTL));
  } else {
    getStorageInstance(storageType).setItem(itemName, storeWithTTL);
  }
};

export const setFromBrowserStorage = (name, store, storageType = STORAGE_TYPE.SESSION) => {
  const rawStore = getFromBrowserStorage(name, true, storageType);
  for (const key in rawStore) {
    store[key] = rawStore[key];
  }
};

export const removeFromBrowserStorage = (name, storageType = STORAGE_TYPE.SESSION) => {
  try {
    const itemName = getItemName(name, storageType);
    if (storageType === STORAGE_TYPE.LOCAL) {
      window.localStorage.removeItem(itemName);
    } else {
      getStorageInstance(storageType).removeItem(itemName);
    }
  } catch (error) {}
};

export const getFromBrowserStorage = (name, isThrow = true, storageType = STORAGE_TYPE.SESSION) => {
  const itemName = getItemName(name, storageType);
  let rawStore;
  
  if (storageType === STORAGE_TYPE.LOCAL) {
    const item = window.localStorage.getItem(itemName);
    rawStore = item ? JSON.parse(item) : null;
  } else {
    rawStore = sessionEncryptStorage.getItem(itemName);
  }
  
  if (rawStore) {
    const { ttl } = rawStore;
    if (ttl) {
      // Check that store ttl has not expired
      const currentTime = new Date().getTime();
      if (ttl === -1 || ttl > currentTime) {
        return rawStore;
      }
      
      if (storageType === STORAGE_TYPE.LOCAL) {
        window.localStorage.removeItem(itemName);
      } else {
        sessionEncryptStorage.removeItem(itemName);
      }
      
      if (isThrow) {
        throw `TTL Expired for ${itemName} in ${storageType} storage, value was ${ttl}. Removing from browser storage`;
      }
    }
  } else {
    if (isThrow) {
      throw `Nothing found in ${storageType} storage for ${itemName}`;
    }
  }
  
  return null;
};

export const setTtlFromBrowserStorage = (name, ttl) => {
  const store = getFromBrowserStorage(name, false);
  if (store) {
    store.ttl = ttl;
  }
};

export const createExtendedStore = <T extends Record<string, unknown>>(initialState: T) => {
  const { on, onChange, state, reset } = createStore(initialState);

  const partialReset = (skipProperties: string[] = []) => {
    const currentState = { ...state } as Record<string, unknown>;
    const resetState = { ...initialState } as Record<string, unknown>;

    // Preserve values for properties that should be skipped
    skipProperties.forEach((property) => {
      if (property in currentState && property in resetState) {
        resetState[property] = currentState[property];
      }
    });

    // Apply the reset with preserved values
    Object.keys(resetState).forEach((key) => {
      (state as Record<string, unknown>)[key] = resetState[key];
    });
  };

  return {
    on,
    onChange,
    state,
    reset,
    partialReset,
  };
};

const qualifyStoreName = (name) => {
  return `ico.funnel.${name}`;
};
